<template>
  <ApplicationWrapper
    v-if="hall.details"
    :showTwoActionLines="this.showTwoActionLines"
    @update:actionArea="handleActionAreaChanged"
    @onButtonBackClick="handleButtonBackClick"
    :headerBackTooltip="fromMyBookings ? $t('overview.mybookings_header_backToBookingOverview') : $t('overview.details_header_backToHallOverview')"
    :headerImage="hall.Image"
    :headerTitle="hall.Title">
    <template slot="details">
      <!-- hall images -->
      <v-carousel v-if="needsImageSlider" hide-delimiter-background height="auto">
        <v-carousel-item v-for="(hallImage, index) in hall.details.Images" :key="'hallImage_' + index">
          <v-img :src="base64ImgPrefix + hallImage" max-height="350px" contain />
        </v-carousel-item>
      </v-carousel>

      <v-divider v-if="needsImageSlider" class="my-4" />

      <v-list two-line class="nomargin-firstchild nomargin-lastchild">
        <!-- booked meetings -->
        <Detail
          v-if="hall.booked && hall.BookedDates"
          :appendIconTooltip="$t('overview.mybookings_cancelAllBookings')"
          :expandable="true"
          :prependIcon="'mdi-calendar'">
          <template slot="expansion-header">
            <v-row no-gutters>
              <v-col cols="12" sm="4"> {{ $t('overview.mybookings_bookedMeetings', [hall.BookedDates.length]) }}</v-col>
              <v-col v-if="currentBreakpoint > 1" cols="8" class="text--secondary">
                <v-row no-gutters style="width: 100%">
                  <v-spacer />
                  <v-col cols="6">
                    {{ $t('overview.label_nextMeeting', [getNextMeetingDateHall(hall)]) }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>

          <template slot="expansion-content">
            <v-list-item three-line dense v-for="(bookedDate, index) of hall.BookedDates" :key="'bookedMeeting_' + hall.Title + '_' + index">
              <v-list-item-avatar v-if="currentBreakpoint > 0">
                <v-icon color="green" v-if="bookedDate.IsPayed">mdi-currency-usd</v-icon>
                <v-icon color="orange" v-else>mdi-currency-usd-off</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <!-- meeting date  -->
                <v-list-item-title> {{ $d(new Date(bookedDate.Date), 'longDate') }} </v-list-item-title>
                <!-- meeting time -->
                <v-list-item-subtitle>
                  <div v-if="!bookedDate.FullTime" v-html="getFancyTimestring(bookedDate.From, bookedDate.To, true, false)" />
                  <div v-else>{{ $t('common.label_meetingWholeDay') }}</div>
                </v-list-item-subtitle>
                <v-list-item-subtitle  style="text-wrap: balance; ">
                  <div v-if="bookedDate.IsPayed">
                    <div class="green--text">{{ $t('common.label_payed') }}</div>
                    <div>{{ $t('common.label_noUnbookingPayedCourses') }}</div>
                  </div>
                  <div v-else class="orange--text">{{ $t('common.label_payment_outstanding') }}</div>
                </v-list-item-subtitle>
              </v-list-item-content>

              <!-- booking cancellation -->
              <v-list-item-action>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn :disabled="bookedDate.IsPayed" @click="handleBookClick(true, bookedDate.Id)" color="error" v-bind="attrs" v-on="on">
                      <v-icon> mdi-cancel </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('overview.details_content_cancelBooking') }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </Detail>

        <!-- description -->
        <Detail
          :expandable="true"
          @onActionIconClick="copyToClipboard(stripHtml(hall.details.Description))"
          :appendIcon="'mdi mdi-content-copy'"
          :appendIconTooltip="$t('common.label_copyToClipboard')"
          :prependIcon="'mdi mdi-information-slab-box-outline'"
          :title="$t('common.label_description')">
          <template slot="expansion-content">
            <div  style="text-wrap: balance; " v-html="hall.details.Description" />
          </template>
        </Detail>

        <!-- hall location -->
        <Detail :prependIcon="'mdi-map-marker'">
          <template slot="content-override">
            <v-list-item-content class="elevation-2 pa-2 rounded" :style="!hall.details.Street ? 'height: 72px' : ''">
              <!-- street -->
              <v-list-item-title>
                {{ hallStreet }}
              </v-list-item-title>

              <!-- meeting date and time -->
              <v-list-item-subtitle>
                {{ hallZipCity }}
              </v-list-item-subtitle>

              <!-- indicator if the address is the clubs address -->
              <v-list-item-subtitle v-if="!hall.details.Street">
                {{ this.$t('overview.details_content_clubAddress') }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action class="align-center elevation-2 pa-2 ma-0 ml-4 rounded" :style="!hall.details.Street ? 'height: 72px' : ''">
              <slot name="actionarea">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-on="on" v-bind="attrs" icon @click="handleOpenMapClick">
                      <v-icon> mdi-map-marker-path </v-icon>
                    </v-btn>
                  </template>
                  <span v-text="$t('overview.details_content_showOnGMaps')"></span>
                </v-tooltip>
              </slot>
            </v-list-item-action>
          </template>
        </Detail>

        <!-- opening hours -->
        <Detail :prependIcon="'mdi-clock-outline'" :title="$t('overview.details_content_openingHours')" :expandable="true">
          <template slot="expansion-header">
            <v-row no-gutters>
              <v-col cols="12" sm="4"> {{ $t('overview.details_content_openingHours') }}</v-col>
              <v-col v-if="currentBreakpoint > 1" cols="8" class="text--secondary">
                <v-row no-gutters style="width: 100%">
                  <v-spacer />
                  <v-col v-if="!getTodaysOpeningHours().IsOpen" cols="6">
                    {{ $t('overview.details_content_openingHours_closedToday') }}
                  </v-col>
                  <v-col v-else cols="6">
                    {{ $t('overview.details_content_openingHours_openedToday') }}
                    {{ getFancyTimestring(getTodaysOpeningHours().From, getTodaysOpeningHours().To, true) }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>

          <template slot="expansion-content">
            <v-list-item dense v-for="(weekDay, index) of hall.details.OpeningHours" :key="'weekDay_' + hall.Title + '_' + index">
              <v-list-item-content>
                <!-- weekday  -->
                <v-list-item-title> {{ $t('weekdays.day_' + weekDay.Day) }} </v-list-item-title>

                <!-- opening hours -->
                <v-list-item-subtitle>
                  <div v-if="weekDay.IsOpen" v-html="getFancyTimestring(weekDay.From, weekDay.To, true)" />
                  <div v-else class="red--text">{{ $t('overview.details_content_openingHours_closed') }}</div>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </Detail>

        <!-- rent -->
        <Detail :prependIcon="'mdi-wallet'" :expandable="true">
          <template slot="expansion-header">
            <v-row no-gutters>
              <v-col cols="12" sm="4">
                <div v-if="hall.details.Rent.PerDay">
                  {{ $t('overview.details_content_rentPerDay') }}
                </div>
                <div v-if="hall.details.Rent.PerHour">
                  {{ $t('overview.details_content_rentPerHour') }}
                </div>
              </v-col>
              <v-col v-if="currentBreakpoint > 1" cols="8" class="text--secondary">
                <v-row no-gutters style="width: 100%">
                  <v-spacer />
                  <v-col cols="6">
                    <div v-if="hall.details.Rent.Amount > 0.0">
                      {{ $t('overview.details_content_rentToday') }}
                      {{ $n(getTodaysRent(), 'currency', 'de-DE') }}
                    </div>
                    <div v-else>
                      {{ $t('overview.details_content_rentToday') }}
                      {{ $t('common.label_free') }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
          <template slot="expansion-content">
            <!-- regular price -->
            <v-list-item dense>
              <v-list-item-content>
                <!-- title  -->
                <v-list-item-title> {{ $t('overview.details_content_regularPrice') }} </v-list-item-title>

                <!-- price -->
                <v-list-item-subtitle>
                  <div v-if="hall.details.Rent.Amount > 0.0">{{ $n(hall.details.Rent.Amount, 'currency', 'de-DE') }}</div>
                  <div v-else>{{ $t('common.label_free') }}</div>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <!-- special rents -->
            <v-list-item dense v-for="(specialRent, index) of hall.details.SpecialRents" :key="index">
              <v-list-item-content>
                <!-- title  -->
                <v-list-item-title> {{ getFullDayName(specialRent.Days) + 's' }} </v-list-item-title>

                <!-- price -->
                <v-list-item-subtitle>
                  <div v-if="specialRent.Amount > 0.0">{{ $n(specialRent.Amount, 'currency', 'de-DE') }}</div>
                  <div v-else>{{ $t('common.label_free') }}</div>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </Detail>

        <!-- types of use -->
        <Detail :prependIcon="'mdi-weight-lifter'" v-if="hall.tags.length > 0">
          <template slot="header">
            <v-list-item-title> {{ $t('overview.details_content_typesOfUse') }}</v-list-item-title>
            <v-list-item-subtitle>
              <v-slide-group show-arrows class="pa-0" id="tagslide">
                <Tag v-for="(tag, index) of hall.tags" :tagCount="hall.tags.length" :key="'tag_hallDetail' + hall.Id + '_' + index" :tagInfo="tag" />
              </v-slide-group>
            </v-list-item-subtitle>
          </template>
        </Detail>
      </v-list>
    </template>

    <template slot="actionbuttons" v-if="SessionData.member.permissions.HallenSchreiben">
      <v-row justify="center" no-gutters>
        <!-- remove booking button -->
        <v-btn
          v-if="canRemoveBooking"
          @click="handleBookClick(true)"
          :loading="isBooking"
          class="ma-2"
          color="error"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          {{ $t('overview.details_action_removeBooking') }}
        </v-btn>

        <!-- add another booking button -->
        <v-btn
          v-if="hall.booked"
          @click="handleBookClick(false)"
          :loading="isBooking"
          class="ma-2"
          color="primary"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          {{ $t('overview.details_action_addAnotherHallBooking') }}
        </v-btn>

        <!-- add booking button (if no meeting is booked)-->
        <v-btn
          v-else
          @click="handleBookClick(false)"
          :loading="isBooking"
          class="ma-2"
          color="primary"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          {{ $t('overview.details_action_addHallBooking') }}
        </v-btn>
      </v-row>
    </template>
    <template slot="actionbuttons" v-else>
      <p class="text-center ma-0" style="align-self: center; text-wrap: balance" v-html="$t('common.label_noBookingPermsHall')" />
    </template>
  </ApplicationWrapper>
  <v-container v-else style="height: 400px">
    <v-row class="fill-height" align-content="center" justify="center">
      <v-col class="text-subtitle-1 text-center" cols="12">
        {{ $t('overview.loading_hallDetails') }}
      </v-col>
      <v-col cols="6">
        <v-progress-linear color="primary" indeterminate rounded height="6"></v-progress-linear>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// vuex
import { mapGetters } from 'vuex';
import { GET_SESSIONDATA } from '@/store/storeMutator';
// mixins
import HelpersMixin from '@/mixins/helpers.js';
import TagsMixin from '@/mixins/tags.js';
import ResponsiveMixin from '@/mixins/responsive.js';
import Constants from '@/mixins/constants.js';

// components
import ApplicationWrapper from '@/components/controls/ApplicationWrapper.vue';
import Detail from '@/components/controls/Detail.vue';
import Tag from '@/components/controls/Tag.vue';

export default {
  name: 'HallDetails',

  emits: ['onBookingClick', 'onButtonBackClick'],

  props: {
    hallId: String,
    isBooking: Boolean,
    fromMyBookings: Boolean,
  },

  mixins: [HelpersMixin, TagsMixin, ResponsiveMixin, Constants],

  components: {
    ApplicationWrapper,
    Detail,
    Tag,
  },

  data: () => {
    return {
      canShowDetails: false,
      showTwoActionLines: undefined,
    };
  },

  methods: {
    handleActionAreaChanged(show) {
      this.showTwoActionLines = show;
    },

    handleBookClick(removeBooking, meetingId = '') {
      if (!Array.isArray(meetingId)) meetingId = [meetingId];
      this.$emit('onBookingClick', this.hall, removeBooking, meetingId);
    },

    handleButtonBackClick() {
      this.$emit('onButtonBackClick');
    },

    handleOpenMapClick() {
      var googleMapsPrefix = 'https://www.google.com/maps/search/';

      // Get address information of the hall. If not available, use the club's address information
      var clubName = this.hall.details.ClubName ? this.hall.details.ClubName : this.SessionData.club.name;
      var street = this.hall.details.Street ? this.hall.details.Street : this.SessionData.club.street;
      var zip = this.hall.details.PostCode ? this.hall.details.PostCode : this.SessionData.club.zip;
      var city = this.hall.details.City ? this.hall.details.City : this.SessionData.club.city;

      // add clubname
      if (this.isNoWs(clubName)) googleMapsPrefix = googleMapsPrefix.concat('+', clubName);

      // add street
      if (!this.isNoWs(street)) googleMapsPrefix = googleMapsPrefix.concat('+', street);

      // add zip
      if (!this.isNoWs(zip)) googleMapsPrefix = googleMapsPrefix.concat('+', zip);

      // add city
      if (!this.isNoWs(city)) googleMapsPrefix = googleMapsPrefix.concat('+', city);

      // remove first occurrence of '+' after the last '/'
      googleMapsPrefix = googleMapsPrefix.replace('+', '').replaceAll(' ', '+');

      // open google maps on a new page
      window.open(googleMapsPrefix, '_blank');
    },

    handleCancleAllBookings() {
      // TODO: cancel all bookings
    },

    getTodaysOpeningHours() {
      var currentDay = new Date().getDay();
      var openingHour = this.hall.details.OpeningHours[currentDay];
      return openingHour;
    },

    getTodaysRent() {
      var currentDay = new Date().getDay();
      var specialRents = this.hall.details.SpecialRents;
      var specialRentToday = specialRents.filter((specialRent) => this.getFullDayName(specialRent.Days, true) == currentDay)[0];

      if (!specialRentToday) {
        return this.hall.details.Rent.Amount;
      }

      return specialRentToday.Amount;
    },
  },

  computed: {
    ...mapGetters({ SessionData: GET_SESSIONDATA }),

    hall() {
      return this.SessionData.club.halls.filter((hall) => hall.Id == this.hallId)[0];
    },

    /** returns whether the hall has images or not (to show/not show the image slider) */
    needsImageSlider() {
      if (!this.hall) return false;
      if (!this.hall.details) return false;
      if (!this.hall.details.Images) return false;

      return this.hall.details.Images.length > 0;
    },

    hallStreet() {
      if (!this.hall.details.Street) return this.clubStreet;

      return this.hall.details.Street;
    },

    clubStreet() {
      return this.SessionData.club.street;
    },

    hallZipCity() {
      if (!this.hall.details.PostCode && !this.hall.details.City) return this.SessionData.club.zip + this.spacing + this.SessionData.club.city;

      return this.hall.details.PostCode + this.spacing + this.hall.details.City;
    },

    canRemoveBooking() {
      if (!this.hall.booked) return false;

      this.hall.BookedDates.filter((bookedDate) => !bookedDate.IsPayed).length > 0;

      return this.hall.BookedDates.filter((bookedDate) => !bookedDate.IsPayed).length > 0;
    },
  },

  watch: {
    isSaving(oldVal, newVal) {
      if (oldVal !== '' && newVal === '') {
        this.step++;
      }
    },
  },
};
</script>

<style scoped>
#tagslide >>> .v-slide-group__wrapper {
  width: 0px !important;
}
</style>
